import { Component, OnInit, ElementRef, OnDestroy, Output, EventEmitter, inject, ViewChild } from '@angular/core';
import { Product, Cart, CartCreateDto, MapperSaleProduct, buildAddSource } from '../../../../dto';
import { Utils } from '../../../../common';
import { CartQuantityValidator } from '../../../../common/composables';
import { CartService, ProductService, GaService, UtmStorageService } from '../../../../services';
import { BaseComponent } from '../../../../core';
import * as CartStore from '../../../../store/cart';
import * as IndicatorStore from '../../../../store/indicator';
import { trigger, transition, style, animate } from '@angular/animations';
import $ from 'jquery';
import * as SelectProductStore from '../../../../store/select-product';
import { takeWhile } from 'rxjs/operators';
import { PRODUCT_SHIPPING_TYPE } from './../../../../shared';

@Component({
  selector: 'app-select-product',
  templateUrl: './select-product.component.html',
  styleUrls: ['./select-product.component.scss'],
  animations: [
    trigger('enterAnimation', [
      transition(':enter', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate('150ms', style({ transform: 'translateX(0)', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ transform: 'translateX(0)', opacity: 1 }),
        animate('150ms', style({ transform: 'translateX(100%)', opacity: 0 })),
      ]),
    ]),
  ],
  standalone: false,
})
export class SelectProductComponent extends BaseComponent implements OnInit, OnDestroy {
  selectedProduct: Product;
  isShowAddToCartModal: boolean;
  itemListName?: string;

  @ViewChild('scroll', { read: ElementRef, static: false })
  scroll: ElementRef;

  @Output()
  isOptionSelect = new EventEmitter<any>();

  private cartService = inject(CartService);
  private productService = inject(ProductService);
  private gaService = inject(GaService);
  private utmStorage = inject(UtmStorageService);

  ngOnInit() {
    this.productService.resetSubject$.pipe(takeWhile(() => this.isAlive)).subscribe(async (products) => {
      this.selectedProduct?.mapperSaleProducts.forEach((mapperSaleProduct) => {
        if (products === undefined) {
          mapperSaleProduct.optionCheck = false;
        } else {
          for (let i = 0; i < products.length; i++) {
            if (products[i].id === mapperSaleProduct.id) {
              mapperSaleProduct.optionCheck = true;
              return;
            } else {
              mapperSaleProduct.optionCheck = false;
            }
          }
        }
      });
    });
    this.productService.openOptionSubject$.subscribe((result) => {
      if (result) this.isShowAddToCartModal = false;
    });
    this.store
      .select(SelectProductStore.SELECT_PRODUCT_STORE_NAME)
      .pipe(takeWhile(() => this.isAlive))
      .subscribe((selectProductPayload) => {
        if (selectProductPayload && selectProductPayload.selectType === 0) {
          this.itemListName = selectProductPayload.itemListName;
          this.open(selectProductPayload.product);
          this.store.dispatch(new SelectProductStore.RemoveProduct());
        } else if (selectProductPayload?.selectType === 1) {
          this.selectedProduct = this.productService.mergeInvalidCountry(selectProductPayload.product);
          this.store.dispatch(new SelectProductStore.RemoveProduct());
          this.isShowAddToCartModal = true;
        }
      });
  }

  addToCart(selectedSaleProduct: MapperSaleProduct) {
    const option = selectedSaleProduct.productOption;
    let initialQty = Utils.toNumber(option.minMoq) || 1;
    const rocketStock = option.rocketStock ?? 0;
    if (rocketStock > 0 && initialQty > rocketStock) {
      initialQty = rocketStock;
    }
    const minimalCart = { mapperSaleProduct: selectedSaleProduct, quantity: initialQty } as Cart;
    const resolveShippingType = (c: Cart, qty: number): PRODUCT_SHIPPING_TYPE => {
      const opt = c.mapperSaleProduct?.productOption;
      if (!opt) return PRODUCT_SHIPPING_TYPE.GENERAL;
      if (opt.europeStock > 0) return PRODUCT_SHIPPING_TYPE.UDIRECT;
      if (opt.americaStock > 0) return PRODUCT_SHIPPING_TYPE.ADIRECT;
      const rocketStock = opt.rocketStock ?? 0;
      if (rocketStock <= 0) return PRODUCT_SHIPPING_TYPE.GENERAL;
      if (qty === 0) {
        return opt.minMoq === rocketStock ? PRODUCT_SHIPPING_TYPE.ROCKET : PRODUCT_SHIPPING_TYPE.GENERAL;
      }
      return qty <= rocketStock ? PRODUCT_SHIPPING_TYPE.ROCKET : PRODUCT_SHIPPING_TYPE.GENERAL;
    };

    const result = CartQuantityValidator.checkQuantity(initialQty, minimalCart, resolveShippingType);
    const quantity = result?.adjustedQuantity ?? initialQty;
    const shippingType = result?.shippingType ?? resolveShippingType(minimalCart, initialQty);

    const saleProduct = new CartCreateDto({
      mapperSaleProductId: Utils.toNumber(selectedSaleProduct.id),
      quantity,
      shippingType,
      addSource: buildAddSource(this.itemListName, this.utmStorage.get()),
    });
    this.cartService.addToCart(saleProduct).subscribe(
      (cart) => {
        const dialog = this._openDialog({
          message: cart.isUpdated ? this.i18n.t('CART.ADDED_MIN') : 'The product is already in your cart.',
          close: () => {
            dialog.close();
          },
        });
        this.store.dispatch(new CartStore.AddCart(cart));
        this.gaService.logAddToCart([cart], this.itemListName);
      },
      (error) => {
        this.store.dispatch(new IndicatorStore.HideIndicator('addToCart'));
        this.errors = error;
        const errorDialog = this._openDialog({
          message: this.errors.message,
          close: () => {
            errorDialog.close();
          },
        });
      }
    );
  }

  addToSelect(product) {
    if (product.productStatus === this.PRODUCT_STATUS.OUT_OF_STOCK) {
      return;
    }
    if (this.selectedProduct?.mapperSaleProducts.length > 1) {
      this.productService.addToSelect(product, this.itemListName);
    }
  }

  public open(selectedProduct: Product) {
    if (selectedProduct.mapperSaleProducts.length === 1) {
      this.addToCart(selectedProduct.mapperSaleProducts[0]);
    } else {
      this.selectedProduct = this.productService.mergeInvalidCountry(selectedProduct);
      this.isShowAddToCartModal = true;
      this.scrollHeight();
    }
  }

  onCloseAddToCartModal() {
    this.isShowAddToCartModal = false;
  }

  private scrollHeight() {
    // tslint:disable-next-line: deprecation
    $(document).ready(() => {
      const viewHeight = window.innerHeight - this.scroll.nativeElement.offsetTop - 20;
      this.scroll.nativeElement.style.maxHeight = viewHeight + 'px';
    });
  }
}
